import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiGetHfExamsPatientId } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useSupportedMedicalData } from "../../../catalogs/supported-medical-data";
import { AddButton } from "../../../components/ui-kit/add-button";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { getVariableByName } from "../../../utils/data-utils";
import { useFetch } from "../../../utils/use-fetch";
import { requireNonNull } from "../../../utils/utils";
import { CellGenericValue } from "../common/cell-generic-value";
import { CellGenericValueField } from "../common/cell-generic-value-field";
import { MedData, MedDataItem } from "../common/med-data";
import { PatientSubpage } from "../common/patient-subpage";

export const ExamsTable = observer(function ExamsTable(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const supportedMedData = useSupportedMedicalData();
  const [model] = useState(
    () => new MedData<MedDataItem>([], api.permissions, api.requireUserUUID()),
  );
  const { loading, refetch } = useFetch(
    async ({ isRefetch }) => {
      const response = await apiGetHfExamsPatientId({
        path: { patient_id: patient.patientId },
        meta: { useSessionCache: !isRefetch },
      });
      model.updateData(response.data.medical_data);
    },
    [patient.patientId],
  );

  return (
    <PatientSubpage title={t`Exams`} loading={loading}>
      <DataTable
        filterActions={
          <AddButton
            items={[
              { label: t`Other (ECG / final outcome)`, href: "add-other" },
              { label: t`ECHO`, href: "add-echo" },
              { label: t`RHC`, href: "add-rhc" },
              { label: t`LHC`, href: "add-lhc" },
            ]}
          >
            <Trans>Add exams data</Trans>
          </AddButton>
        }
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        columns={[
          {
            header: <Trans>Measured</Trans>,
            attr: "measurement_datetime",
            type: "datetime",
            editable: true,
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Type</Trans>,
            attr: "measurement_type",
            type: "string",
            sort: true,
          },
          {
            header: <Trans>Value</Trans>,
            attr: "measurement_value",
            editable: true,
            editInDialog: true,
            cell: (p) => (
              <>
                {!p.patch && (
                  <CellGenericValue
                    {...p}
                    attr={requireNonNull(p.attr)}
                    variableDef={getVariableByName(
                      supportedMedData,
                      p.item.measurement_type,
                    )}
                  />
                )}
                {p.patch && (
                  <CellGenericValueField
                    model={p.patch}
                    attr={requireNonNull(p.attr)}
                    variableDef={getVariableByName(
                      supportedMedData,
                      p.item.measurement_type,
                    )}
                  />
                )}
              </>
            ),
          },
          {
            header: <Trans>Unit</Trans>,
            attr: "measurement_unit",
            type: "string",
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            editable: true,
            multiline: true,
          },
          model.showingHistory && {
            header: <Trans>Added</Trans>,
            attr: "created_at",
            type: "datetime",
          },
          model.showingHistory && {
            header: <Trans>Last edit</Trans>,
            cell: ({ item }) => <CellLastEdit item={item} />,
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("medicaldata", item.uuid, editedField);
          await refetch();
        }}
        onDelete={async (item) => {
          await patient.deleteItem("medicaldata", item.uuid);
          await refetch();
        }}
      />
    </PatientSubpage>
  );
});
